import React, { FC, MouseEvent, useCallback } from 'react';

import { isNumber, isUndefined, noop } from 'lodash';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import { ActionButtonVariants } from 'components/action-button';
import { FavoriteIcon } from 'components/favorite-icon';
import { ButtonForIconFav } from 'components/favorite-icon/styled';
import { IconLoyaltyMyCode } from 'components/icons/loyalty-my-code';
import { IconLoyaltyTiersLock } from 'components/icons/loyalty-tiers-lock';
import { IconPhone } from 'components/icons/phone';
import { IconRestaurent } from 'components/icons/restaurent';
import { FavoriteLoading } from 'components/lottie-animations/favorite-loading/favorite-loading';
import { OfferBadges } from 'components/offer-badges';
import { CustomDisclaimer } from 'components/offers/ui-refresh/offer-disclaimer';
import { useEnableOffersBadge } from 'hooks/use-enable-offers-badge';
import { useFeatureInvitationCode } from 'hooks/use-feature-invitation-code';
import { useForceStoreSelectionFlagged } from 'hooks/use-force-store-selection';
import { useMediaQuery } from 'hooks/use-media-query';
import { ShareApiData, useShareApi } from 'hooks/use-share-api/use-share-api';
import { TimeUnit } from 'pages/loyalty/loyalty-banner/types';
import { LoyaltyOffersCooldownDrawer } from 'pages/loyalty/loyalty-offers/loyalty-offers-cooldown-drawer';
import { BackIcon } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/BackIcon';
import { CloseIcon } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/CloseIcon';
import { ComingSoonIcon } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/ComingSoonIcon';
import { FavouriteIconWrapper } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/FavouriteIcon';
import { HeartIcon } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/HeartIcon';
import { LockIcon, LockIconWrapper } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/LockIcon';
import { PromoCodeIcon } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/PromoCodeIcon';
import { ShareIcon } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/ShareIcon';
import { LabelText } from 'pages/loyalty/loyalty-offers/loyalty-offers-v2/loyalty-offers-card.styles';
import { OfferLocked } from 'pages/loyalty/offer-locked/offer-locked';
import { parseAssetID } from 'remote/build-image-url';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  IncentiveButtonVariation,
  defaultIncentiveButtonVariation,
} from 'state/launchdarkly/variations';
import { FixableEvaluationErrors, IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { useFeaturesEarningCalculations } from 'state/loyalty/hooks/use-feature-earning-calculations';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';
import { IncentiveSource } from 'state/loyalty/types';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants/primitives';
import { generateSrc } from 'utils/image';
import { routes } from 'utils/routing';
import { toast } from 'utils/toast';

import { DISABLE_IN_RESTAURANT_FOR_INVALID_SERVICE_MODE } from '../constants';
import { DefaultBackground, PointsNeededText } from '../incentive-card/incentive-card.styled';

import { POINTS_TRANSLATION_KEY, REDEEM_REWARD_TRANSLATION_KEY } from './constants';
import { IncentiveDetailsToast, incentiveToast } from './incentive-details-toast';
import {
  BackgroundImage,
  BadgeContainer,
  BarLockIcon,
  ButtonContainer,
  ButtonContainerV2,
  ButtonsWrapper,
  ButtonsWrapperV2,
  ContentTitle,
  ContentWrapper,
  CrownMeterBar,
  DisclaimerText,
  ErrorMessage,
  FavouriteButtonWrapper,
  FavouriteIconLoadingWrapper,
  Header,
  HeaderTitle,
  IconWrapper,
  ImageWrapper,
  ImageWrapperV2,
  InlineRewardDetail,
  InlineRewardWrapper,
  LeftHeaderButtonsContainer,
  LoadingWrapper,
  LockOverlay,
  OrderMore,
  OrderMoreButton,
  OrderMoreToUnlockRewardButton,
  Padding,
  RemoveRewardsButton,
  RewardDescription,
  RewardDescriptionV2,
  RewardDetailsContainer,
  RewardName,
  RewardNameV2,
  RewardPictureLock,
  RightHeaderButtonsContainer,
  ShareIconWrapper,
  StyledActionButton,
  StyledActionButtonV2,
  StyledActionNotifyMeButton,
  StyledActionNotifyMeButtonV2,
  StyledBellIcon,
  StyledDisclaimerContainer,
  StyledDisclaimerContainerV2,
  StyledInRestaurantIcon,
  StyledIncentiveEvaluationMessages,
  StyledLoyaltyButton,
  StyledLoyaltyButtonSubtitle,
  StyledLoyaltyButtonText,
  StyledLoyaltyButtonTitle,
  StyledMobileOrderIcon,
  StyledMyCodeIcon,
  StyledPhoneIcon,
  StyledPointsDisplay,
  StyledPointsDisplayTierLockedBar,
  StyledProgressMeter,
  StyledRewardImage,
  StyledRewardImageV2,
  StyledSecondaryActionButton,
  StyledSecondaryActionButtonV2,
  StyledUpcomingOfferBadge,
  TierLockedBar,
  TierLockedText,
  TierRequirementTag,
  TierRequirementText,
  Wrapper,
  WrapperButton,
  WrapperButtonV2,
  WrapperV2,
} from './incentive-details.styled';
import { LockedInlineRewardDetails } from './locked-inline-reward-details';
import { IIncentiveDetailsViewProps } from './types';

export const IncentiveDetailsView: FC<IIncentiveDetailsViewProps> = ({
  id,
  name,
  image,
  description,
  moreInfo,
  isOffer,
  pointCost,
  locked,
  tierLocked,
  requiredLoyaltyTier,
  tierPointsNeeded,
  closedOffer,
  remainingPointsNeededForRedemption,
  redemptionPercentage,
  handleRedeemRewardClick,
  handleInRestaurantRedemptionClick,
  errorMessage,
  checkingAvailability,
  backgroundImage,
  canAddToInRestaurantOrder = false,
  canAddToMobileOrder = false,
  showRemoveRewardButton = false,
  handleRemoveRewardsClick,
  incentiveEvaluationResults,
  offersInCooldown = false,
  favorited = false,
  favoriteLoading = false,
  handleToggleFavorite = noop,
  challengeIncentiveSelected = false,
  incentiveEndingIn,
  isUpcomingOffer,
  isWidget,
  isInlineRewards,
  isV2,
  metadata,
  onGoBack,
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  useForceStoreSelectionFlagged();
  const { isAuthenticated, setOriginLocation } = useAuthContext();
  const { data, getContentForTier } = useFeaturesLoyaltyTiersContent();
  const { getTierEarningData } = useFeaturesEarningCalculations();
  const location = useLocation();
  const cdp = useCdpContext();
  const { language } = useLocale();
  const { noStoreSelected } = useStoreContext();
  const isMobile = useMediaQuery('mobile');

  const meterEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_METER_ON_REWARDS));
  const enableFavoriteOffers = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_OFFERS);
  const isWhiteBackgroundEnabled = useFlag(LaunchDarklyFlag.ENABLE_WHITE_BACKGROUND);
  const enableABTestingOfferAndRewardDetailsButtons =
    useFlag<IncentiveButtonVariation>(
      LaunchDarklyFlag.ENABLE_AB_TESTING_OFFER_AND_REWARD_DETAILS_BUTTONS
    ) || defaultIncentiveButtonVariation;
  const enableABTestingOfferAndRewardDetailsButtonsDesign = useFlag(
    LaunchDarklyFlag.ENABLE_AB_TESTING_OFFER_AND_REWARD_DETAILS_BUTTONS_DESIGN
  );

  const { featureInvitationCode } = useFeatureInvitationCode();
  const { enableOfferBadges } = useEnableOffersBadge();

  const incentiveLocked = locked || tierLocked || closedOffer;

  const questBenefit =
    featureInvitationCode?.invitationCodeBenefit && featureInvitationCode?.invitationCodeBenefit[0];

  const incentiveDetails = {
    name: challengeIncentiveSelected ? questBenefit?.name?.locale : name,
    description: challengeIncentiveSelected ? questBenefit?.howToRedeem?.localeRaw : description,
    image: challengeIncentiveSelected ? questBenefit?.image?.locale : image,
    backgroundImage: <DefaultBackground />,
  };

  // Offers should show "Add to Order" while Rewards should show "Redeem Reward"
  let redeemText = formatMessage({
    id: isOffer ? 'addMobileOrder' : REDEEM_REWARD_TRANSLATION_KEY,
  });

  if (enableABTestingOfferAndRewardDetailsButtons?.onlineOrderButton) {
    redeemText =
      enableABTestingOfferAndRewardDetailsButtons?.onlineOrderButton[language] || redeemText;
  }

  let inRestaurantRedemptionText = formatMessage({ id: 'redeemInRestaurant' });

  if (enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton) {
    inRestaurantRedemptionText =
      enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton[language] ||
      inRestaurantRedemptionText;
  }

  const RewardWrapper: FC<{ children: React.ReactNode }> = useCallback(
    ({ children }) => {
      if (isInlineRewards) {
        return (
          <InlineRewardWrapper>
            {!isMobile && (
              <InlineRewardDetail>{formatMessage({ id: 'rewardDetail' })}</InlineRewardDetail>
            )}
            {children}
          </InlineRewardWrapper>
        );
      }
      return <>{children}</>;
    },
    [formatMessage, isInlineRewards, isMobile]
  );

  const hasServiceModeError = (
    evaluationResults: IIncentiveDetailsViewProps['incentiveEvaluationResults']
  ) =>
    !!evaluationResults?.some(
      evaluationResult =>
        evaluationResult.code === IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE
    );

  const handleFavoriteClick = useCallback(
    (event: MouseEvent<HTMLElement>, successCallback?: (message: string) => void) => {
      event.preventDefault();
      event.stopPropagation();
      if (isV2 && !isAuthenticated()) {
        return navigate(routes.signUp);
      }
      handleToggleFavorite(id, name, successCallback);
    },
    [handleToggleFavorite, id, name, isAuthenticated, navigate, isV2, isInlineRewards]
  );

  const handleOrderMoreClick = useCallback(() => {
    if (data?.loyaltyTiersOrderMoreButtonLink?.locale) {
      cdp.trackEvent({
        name: CustomEventNames.BUTTON_CLICK,
        type: EventTypes.Other,
        attributes: {
          Name: 'Order More to become a Superking',
          Path: location.pathname,
        },
      });

      return navigate(data?.loyaltyTiersOrderMoreButtonLink?.locale);
    }
  }, [data?.loyaltyTiersOrderMoreButtonLink?.locale, navigate, cdp, location.pathname]);

  const handleRedirectToSignUp = useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault();
      setOriginLocation(location.pathname);
      navigate(routes.signUp);
    },
    [location.pathname, navigate, setOriginLocation]
  );

  const handleSubscribeOffer = useCallback(() => {
    toast.success(formatMessage({ id: 'offerUpcomingConfirmation' }));
  }, [formatMessage]);

  const handleSubscribeOfferV2 = useCallback(() => {
    incentiveToast(formatMessage({ id: 'offerUpcomingConfirmation' }));
  }, [formatMessage]);

  const hasBlockingErrors =
    !!errorMessage ||
    (offersInCooldown && isOffer) ||
    // Disable buttons if Feedback is not included in fixable incentive errors
    incentiveEvaluationResults?.some(
      evaluationResult =>
        !FixableEvaluationErrors.has(evaluationResult.code as IncentiveEvaluationErrorCodes)
    );

  const disableMobileOrderButton =
    hasBlockingErrors ||
    hasServiceModeError(incentiveEvaluationResults) ||
    challengeIncentiveSelected;

  const disableInRestaurantOrderButton =
    hasBlockingErrors ||
    (DISABLE_IN_RESTAURANT_FOR_INVALID_SERVICE_MODE &&
      hasServiceModeError(incentiveEvaluationResults)) ||
    challengeIncentiveSelected;

  const getSafeTimeUnit = (unit: keyof typeof TimeUnit | undefined): TimeUnit | '' => {
    if (unit && Object.keys(TimeUnit).includes(unit)) {
      return TimeUnit[unit];
    }
    return '';
  };

  const isSVG = (format: string) => format === 'svg';

  const { buildImageUrl } = useUIContext();

  const fetchImageScr = useCallback(() => {
    if (!incentiveDetails.image) {
      return null;
    }

    const { format } = parseAssetID(incentiveDetails.image?.asset?._id);

    if (isSVG(format)) {
      const src = buildImageUrl(incentiveDetails.image);
      return src;
    }

    // Using `generateSrc` for a single image URL
    const src = generateSrc({
      buildImageUrl,
      image: incentiveDetails.image,
      device: {
        width: 1600,
        dpr: 2,
      },
      quality: 100,
      format: 'webp',
      auto: 'format',
    });
    return src;
  }, [incentiveDetails, buildImageUrl]);

  const entranceData = getTierEarningData(requiredLoyaltyTier);
  const timeUnitTarget = getSafeTimeUnit(
    entranceData?.pointsToEnterCalculationTimeUnit as keyof typeof TimeUnit
  );

  const isReversedButtons = Boolean(
    noStoreSelected &&
      (enableABTestingOfferAndRewardDetailsButtons.onlineOrderButton ||
        enableABTestingOfferAndRewardDetailsButtonsDesign)
  );

  const mobileOrderIconFill = disableMobileOrderButton
    ? Styles.color.grey.three
    : isReversedButtons
      ? Styles.color.primary
      : Styles.color.white;

  // Button variant logic is updated from existing code of StyledSecondaryActionButtonV2 -> Variant (697)
  let buttonVariant: ActionButtonVariants;

  if (canAddToMobileOrder) {
    if (enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton) {
      if (isReversedButtons) {
        buttonVariant = ActionButtonVariants.PRIMARY;
      } else {
        buttonVariant = ActionButtonVariants.OUTLINE;
      }
    } else {
      buttonVariant = ActionButtonVariants.TEXT_ONLY;
    }
  } else {
    buttonVariant = ActionButtonVariants.PRIMARY;
  }

  let myCodeIconFill: string | undefined;

  if (disableInRestaurantOrderButton) {
    myCodeIconFill = Styles.color.grey.three;
  } else if (
    !isReversedButtons &&
    !canAddToMobileOrder &&
    !enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton
  ) {
    myCodeIconFill = Styles.color.red;
  } else if (isWhiteBackgroundEnabled && buttonVariant === ActionButtonVariants.OUTLINE) {
    myCodeIconFill = Styles.color.primary;
  } else {
    myCodeIconFill = undefined;
  }

  const { canShare, shareData } = useShareApi();

  if (isV2 && !isInlineRewards) {
    const showPromoCodeLabel = metadata?.some(
      meta => meta?.value === IncentiveSource.PROMOTION_CODE
    );

    const promoCodeLabel = showPromoCodeLabel
      ? formatMessage({ id: 'promoCodeBadgeLabel', defaultMessage: 'Promo Code' })
      : undefined;

    const renderOfferLabel = () => {
      if (promoCodeLabel) {
        return (
          <BadgeContainer>
            <PromoCodeIcon width="13.33px" height="13.33px" />
            <LabelText>{promoCodeLabel}</LabelText>
          </BadgeContainer>
        );
      }

      if (isUpcomingOffer) {
        return (
          <BadgeContainer>
            <ComingSoonIcon width="13.33px" height="13.33px" />
            <LabelText>
              {formatMessage({ id: 'comingSoon', defaultMessage: 'Coming Soon' })}
            </LabelText>
          </BadgeContainer>
        );
      }

      if (incentiveLocked || challengeIncentiveSelected) {
        return (
          <BadgeContainer>
            <LockIconWrapper>
              <LockIcon width="18.9px" height="14.4px" />
            </LockIconWrapper>
            <LabelText>
              {formatMessage({ id: 'lockedOfferLabel', defaultMessage: 'Locked' })}
            </LabelText>
          </BadgeContainer>
        );
      }

      return null;
    };

    const handleShare = async () => {
      // Tracking Event
      cdp.trackEvent({
        name: CustomEventNames.BUTTON_CLICK,
        type: EventTypes.Other,
        attributes: {
          Name: 'Share Offer',
          Path: location.pathname,
          offerId: id,
        },
      });

      const imageSrc: string | null = fetchImageScr();
      let file = null;

      if (imageSrc) {
        const response = await fetch(imageSrc);
        const blob = await response.blob();
        file = new File([blob], `offer-${id}.webp`, { type: blob.type });
      }

      const dataToShare: ShareApiData = {
        title: formatMessage({ id: 'offerDetail', defaultMessage: 'Offer Detail' }),
        text: incentiveDetails?.name ?? '',
        url: location.pathname,
        files: file ? [file] : [],
      };

      try {
        await shareData(dataToShare);
      } catch (e) {
        const errorString = e.toString() || '';
        if (errorString.includes('AbortError') || errorString.includes('canceled')) {
          return;
        }
      }
    };

    return (
      <WrapperV2>
        <Header>
          <LeftHeaderButtonsContainer>
            {isMobile ? (
              <IconWrapper onClick={() => onGoBack && onGoBack()}>
                <BackIcon height="1rem" width="1rem" />
              </IconWrapper>
            ) : (
              <IconWrapper onClick={() => onGoBack && onGoBack()}>
                <CloseIcon height="1rem" width="1rem" />
              </IconWrapper>
            )}
          </LeftHeaderButtonsContainer>
          {isMobile && (
            <HeaderTitle>
              {formatMessage({ id: 'offerDetail', defaultMessage: 'Offer Detail' })}
            </HeaderTitle>
          )}
          <RightHeaderButtonsContainer>
            {enableFavoriteOffers && isAuthenticated() && isOffer && (
              <WrapperButtonV2 favoriteLoading={favoriteLoading}>
                <FavouriteButtonWrapper
                  data-testid={favorited ? 'fav-icon-selected' : 'fav-icon'}
                  onClick={e => {
                    const successCb = (message: string) => {
                      incentiveToast(message);
                    };
                    handleFavoriteClick(e, isMobile ? successCb : undefined);
                  }}
                  aria-label={formatMessage({
                    id: favorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
                  })}
                  isTransparent={true}
                >
                  {favoriteLoading ? (
                    <FavouriteIconLoadingWrapper>
                      <FavoriteLoading height="2.4rem" width="2.4rem" />
                    </FavouriteIconLoadingWrapper>
                  ) : (
                    <FavouriteIconWrapper>
                      <HeartIcon
                        height="1.375rem"
                        width="1.5rem"
                        fillColor={favorited ? '#d62300' : ''}
                      ></HeartIcon>
                    </FavouriteIconWrapper>
                  )}
                </FavouriteButtonWrapper>
              </WrapperButtonV2>
            )}
            {canShare && (
              <ShareIconWrapper onClick={handleShare}>
                <ShareIcon height="1.375rem" width="1rem" />
              </ShareIconWrapper>
            )}
          </RightHeaderButtonsContainer>
        </Header>
        <ContentWrapper>
          {!isMobile && (
            <ContentTitle>
              {formatMessage({ id: 'offerDetail', defaultMessage: 'Offer Detail' })}
            </ContentTitle>
          )}
          <ImageWrapperV2>
            {challengeIncentiveSelected
              ? incentiveDetails.backgroundImage
              : backgroundImage && (
                  <BackgroundImage
                    alt={name}
                    image={backgroundImage}
                    isChallengeIncentive={challengeIncentiveSelected}
                  />
                )}
            {incentiveDetails.image && (
              <StyledRewardImageV2
                alt={name}
                image={incentiveDetails.image}
                objectFitContain
                isChallengeIncentive={challengeIncentiveSelected}
                quality={100}
              />
            )}
            {(incentiveLocked && !isUpcomingOffer) || challengeIncentiveSelected ? (
              <>
                <LockOverlay />
                <RewardPictureLock fill={primitive.$white} />
              </>
            ) : null}
          </ImageWrapperV2>
          {renderOfferLabel()}
          {incentiveDetails.name && (
            <RewardNameV2 data-testid="reward-name">{incentiveDetails.name}</RewardNameV2>
          )}
          {incentiveDetails.description && (
            <RewardDescriptionV2 content={incentiveDetails.description} />
          )}
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          <ButtonsWrapperV2>
            {isUpcomingOffer ? (
              <ButtonContainerV2>
                {isAuthenticated() ? (
                  <StyledActionNotifyMeButtonV2
                    data-testid="notify-when-available"
                    onClick={handleSubscribeOfferV2}
                    eventAttributes={{
                      Name: 'Notify Me When Available',
                      Path: location.pathname,
                      sanityId: id,
                    }}
                    startIcon={<StyledBellIcon fill={Styles.color.white} />}
                  >
                    {formatMessage({ id: 'notifyWhenAvailableButton' })}
                  </StyledActionNotifyMeButtonV2>
                ) : (
                  <StyledActionNotifyMeButtonV2
                    data-testid="sign-up-to-notify"
                    onClick={handleRedirectToSignUp}
                    eventAttributes={{
                      Name: 'Sign Up to Notify Me When Available',
                      Path: location.pathname,
                    }}
                    startIcon={<StyledBellIcon fill={Styles.color.white} />}
                  >
                    {formatMessage({ id: 'signUpToNotifyWhenAvailable' })}
                  </StyledActionNotifyMeButtonV2>
                )}
              </ButtonContainerV2>
            ) : null}

            {!locked && !tierLocked && !isUpcomingOffer && (
              <ButtonContainerV2 reversed={isReversedButtons}>
                {canAddToMobileOrder && !showRemoveRewardButton && (
                  <>
                    {enableABTestingOfferAndRewardDetailsButtonsDesign ? (
                      <StyledLoyaltyButton
                        onClick={handleRedeemRewardClick}
                        disabled={disableMobileOrderButton}
                        data-testid="redeem-to-mobile-order-btn-v2"
                      >
                        <StyledMobileOrderIcon>
                          <IconPhone fill={primitive.$white} />
                        </StyledMobileOrderIcon>
                        <StyledLoyaltyButtonText>
                          <StyledLoyaltyButtonTitle>
                            {formatMessage({ id: 'loyaltyRedeemAddToMobileOrder' })}
                          </StyledLoyaltyButtonTitle>
                          <StyledLoyaltyButtonSubtitle>
                            {formatMessage({ id: 'loyaltyRedeemAddToMobileOrderDescription' })}
                          </StyledLoyaltyButtonSubtitle>
                        </StyledLoyaltyButtonText>
                      </StyledLoyaltyButton>
                    ) : (
                      <StyledActionButtonV2
                        disabled={disableMobileOrderButton}
                        onClick={handleRedeemRewardClick}
                        isLoading={checkingAvailability}
                        data-testid="redeem-to-mobile-order-btn-v2"
                        startIcon={
                          enableABTestingOfferAndRewardDetailsButtons?.onlineOrderButton ? (
                            <StyledPhoneIcon fill={mobileOrderIconFill} />
                          ) : null
                        }
                        variant={
                          isReversedButtons
                            ? ActionButtonVariants.OUTLINE
                            : ActionButtonVariants.PRIMARY
                        }
                        legacyStyle={Boolean(
                          !enableABTestingOfferAndRewardDetailsButtons?.onlineOrderButton
                        )}
                      >
                        {redeemText}
                      </StyledActionButtonV2>
                    )}
                  </>
                )}
                {canAddToInRestaurantOrder && (
                  <>
                    {enableABTestingOfferAndRewardDetailsButtonsDesign ? (
                      <StyledLoyaltyButton
                        onClick={handleInRestaurantRedemptionClick}
                        disabled={disableInRestaurantOrderButton}
                        data-testid="redeem-in-restaurant-btn-v2"
                      >
                        <StyledInRestaurantIcon>
                          <IconLoyaltyMyCode />
                        </StyledInRestaurantIcon>
                        <StyledLoyaltyButtonText>
                          <StyledLoyaltyButtonTitle>
                            {formatMessage({ id: 'loyaltyRedeemScanInRestaurant' })}
                          </StyledLoyaltyButtonTitle>
                          <StyledLoyaltyButtonSubtitle>
                            {formatMessage({ id: 'loyaltyRedeemScanInRestaurantDescription' })}
                          </StyledLoyaltyButtonSubtitle>
                        </StyledLoyaltyButtonText>
                      </StyledLoyaltyButton>
                    ) : (
                      <StyledSecondaryActionButtonV2
                        disabled={disableInRestaurantOrderButton}
                        onClick={handleInRestaurantRedemptionClick}
                        isLoading={checkingAvailability}
                        data-testid="redeem-in-restaurant-btn-v2"
                        startIcon={
                          enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton ? (
                            <StyledMyCodeIcon fill={myCodeIconFill} />
                          ) : null
                        }
                        variant={
                          canAddToMobileOrder
                            ? enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton
                              ? isReversedButtons
                                ? ActionButtonVariants.PRIMARY
                                : ActionButtonVariants.OUTLINE
                              : ActionButtonVariants.TEXT_ONLY
                            : ActionButtonVariants.PRIMARY
                        }
                        legacyStyle={Boolean(
                          !enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton
                        )}
                      >
                        {inRestaurantRedemptionText}
                      </StyledSecondaryActionButtonV2>
                    )}
                  </>
                )}
              </ButtonContainerV2>
            )}
          </ButtonsWrapperV2>

          {incentiveEvaluationResults && (
            <StyledIncentiveEvaluationMessages
              incentiveEvaluationResults={incentiveEvaluationResults}
              hideIcon
            />
          )}
          {isOffer && moreInfo && (
            <StyledDisclaimerContainerV2>
              <CustomDisclaimer content={moreInfo} />
            </StyledDisclaimerContainerV2>
          )}
        </ContentWrapper>
        {isMobile && <IncentiveDetailsToast />}
      </WrapperV2>
    );
  }

  return locked && isInlineRewards ? (
    <LockedInlineRewardDetails
      pointCost={pointCost}
      remainingPointsNeededForRedemption={remainingPointsNeededForRedemption}
      redemptionPercentage={redemptionPercentage}
      incentiveDetails={incentiveDetails}
    />
  ) : (
    <Wrapper>
      <RewardWrapper>
        <ImageWrapper hasMargin={isWidget}>
          {enableFavoriteOffers && isOffer && (
            <WrapperButton>
              <ButtonForIconFav
                data-testid={favorited ? 'fav-icon-selected' : 'fav-icon'}
                onClick={handleFavoriteClick}
                aria-label={formatMessage({
                  id: favorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
                })}
                isTransparent={false}
              >
                {favoriteLoading ? (
                  <LoadingWrapper>
                    <FavoriteLoading />
                  </LoadingWrapper>
                ) : (
                  <FavoriteIcon isFavorite={favorited} />
                )}
              </ButtonForIconFav>
            </WrapperButton>
          )}

          {challengeIncentiveSelected
            ? incentiveDetails.backgroundImage
            : backgroundImage && (
                <BackgroundImage
                  alt={name}
                  image={backgroundImage}
                  isChallengeIncentive={challengeIncentiveSelected}
                />
              )}
          {incentiveDetails.image && (
            <StyledRewardImage
              alt={name}
              image={incentiveDetails.image}
              objectFitContain
              isChallengeIncentive={challengeIncentiveSelected}
              quality={100}
            />
          )}
          {challengeIncentiveSelected && <OfferLocked />}
          {incentiveLocked && !isUpcomingOffer ? (
            <>
              <LockOverlay />
              <RewardPictureLock fill={primitive.$white} />
            </>
          ) : null}
        </ImageWrapper>

        <RewardDetailsContainer>
          {!isUpcomingOffer && enableOfferBadges && (
            <OfferBadges displayLoginRequiredBadge={false} endingIn={incentiveEndingIn} />
          )}
          {isUpcomingOffer && <StyledUpcomingOfferBadge />}
          {!isUndefined(pointCost) && !tierLocked && (
            <StyledPointsDisplay
              value={pointCost}
              suffix={formatMessage({ id: POINTS_TRANSLATION_KEY })}
            />
          )}
          {tierLocked && (
            <TierRequirementTag>
              <IconLoyaltyTiersLock />
              <TierRequirementText>
                {getContentForTier(requiredLoyaltyTier!)?.loyaltyTiersUIConfigName?.locale}
              </TierRequirementText>
            </TierRequirementTag>
          )}
          {incentiveDetails.name && (
            <RewardName data-testid="reward-name">{incentiveDetails.name}</RewardName>
          )}
          {incentiveDetails.description && (
            <RewardDescription content={incentiveDetails.description} />
          )}
          {locked &&
            (isInlineRewards ? (
              <>
                <OrderMore>
                  <StyledPointsDisplayTierLockedBar
                    value={formatMessage({ id: 'numberOfCrowns' }, { crowns: pointCost })}
                    icon={<BarLockIcon fill={primitive.bk.$fieryRed} />}
                  />
                  {meterEnabled && isNumber(redemptionPercentage) && (
                    <CrownMeterBar percentage={redemptionPercentage} />
                  )}
                  {isNumber(remainingPointsNeededForRedemption) && (
                    <PointsNeededText>
                      {formatMessage(
                        { id: 'yourXPointsAway' },
                        {
                          points: (
                            <b>
                              <StyledPointsDisplayTierLockedBar
                                value={remainingPointsNeededForRedemption}
                                icon={null}
                              />
                            </b>
                          ),

                          pointsSlang: POINTS_TRANSLATION_KEY,
                        }
                      )}
                    </PointsNeededText>
                  )}
                </OrderMore>
                <OrderMoreToUnlockRewardButton
                  data-testid="order-more-unlock-reward"
                  to={routes.menu}
                  onClick={() => navigate(routes.menu)}
                >
                  <IconRestaurent fill="white" />
                  {formatMessage({ id: 'orderMoreToUnlockReward' })}
                </OrderMoreToUnlockRewardButton>
              </>
            ) : (
              <>
                {meterEnabled && isNumber(redemptionPercentage) && (
                  <StyledProgressMeter percentage={redemptionPercentage} />
                )}
                {isNumber(remainingPointsNeededForRedemption) && (
                  <PointsNeededText>
                    {formatMessage(
                      { id: 'yourXPointsAway' },
                      {
                        points: <span>{remainingPointsNeededForRedemption}</span>,
                        pointsSlang: POINTS_TRANSLATION_KEY,
                      }
                    )}
                  </PointsNeededText>
                )}
              </>
            ))}

          {!isOffer && moreInfo && <DisclaimerText content={moreInfo} />}
          {/* padding element needed for safari / iOS native devices */}
          <Padding />
        </RewardDetailsContainer>

        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        {tierLocked && tierPointsNeeded! > 0 && (
          <TierLockedBar>
            <TierLockedText>
              {formatMessage(
                { id: `loyaltyTierEarnMorePoints${timeUnitTarget}` },
                {
                  points: tierPointsNeeded,
                  pointsLabel: formatMessage({ id: 'points' }),
                  timeCount: entranceData?.pointsToEnterCalculationTimeInterval,
                  tierName: getContentForTier(requiredLoyaltyTier!)?.loyaltyTiersUIConfigName
                    ?.locale,
                }
              )}
            </TierLockedText>
            <StyledPointsDisplayTierLockedBar
              value={formatMessage({ id: 'numberOfCrowns' }, { crowns: tierPointsNeeded })}
              icon={<BarLockIcon fill={primitive.bk.$fieryRed} />}
            />
            <StyledProgressMeter percentage={redemptionPercentage!} />
          </TierLockedBar>
        )}

        <ButtonsWrapper>
          {isUpcomingOffer ? (
            <ButtonContainer>
              {isAuthenticated() ? (
                <StyledActionNotifyMeButton
                  data-testid="notify-when-available"
                  onClick={handleSubscribeOffer}
                  eventAttributes={{
                    Name: 'Notify Me When Available',
                    Path: location.pathname,
                    sanityId: id,
                  }}
                >
                  {formatMessage({ id: 'notifyWhenAvailableButton' })}
                </StyledActionNotifyMeButton>
              ) : (
                <StyledActionNotifyMeButton
                  data-testid="sign-up-to-notify"
                  onClick={handleRedirectToSignUp}
                  eventAttributes={{
                    Name: 'Sign Up to Notify Me When Available',
                    Path: location.pathname,
                  }}
                >
                  {formatMessage({ id: 'signUpToNotifyWhenAvailable' })}
                </StyledActionNotifyMeButton>
              )}
            </ButtonContainer>
          ) : null}

          {!locked && !tierLocked && !isUpcomingOffer && (
            <ButtonContainer reversed={isReversedButtons}>
              {canAddToMobileOrder && !showRemoveRewardButton && (
                <>
                  {enableABTestingOfferAndRewardDetailsButtonsDesign ? (
                    <StyledLoyaltyButton
                      onClick={handleRedeemRewardClick}
                      disabled={disableMobileOrderButton}
                      data-testid="redeem-to-mobile-order-btn"
                    >
                      <StyledMobileOrderIcon>
                        <IconPhone fill={primitive.$white} />
                      </StyledMobileOrderIcon>
                      <StyledLoyaltyButtonText>
                        <StyledLoyaltyButtonTitle>
                          {formatMessage({ id: 'loyaltyRedeemAddToMobileOrder' })}
                        </StyledLoyaltyButtonTitle>
                        <StyledLoyaltyButtonSubtitle>
                          {formatMessage({ id: 'loyaltyRedeemAddToMobileOrderDescription' })}
                        </StyledLoyaltyButtonSubtitle>
                      </StyledLoyaltyButtonText>
                    </StyledLoyaltyButton>
                  ) : (
                    <StyledActionButton
                      isWhiteBackgroundEnabled={isWhiteBackgroundEnabled}
                      disabled={disableMobileOrderButton}
                      onClick={handleRedeemRewardClick}
                      isLoading={checkingAvailability}
                      data-testid="redeem-to-mobile-order-btn"
                      startIcon={
                        enableABTestingOfferAndRewardDetailsButtons?.onlineOrderButton ? (
                          <StyledPhoneIcon fill={mobileOrderIconFill} />
                        ) : null
                      }
                      variant={
                        isReversedButtons
                          ? ActionButtonVariants.OUTLINE
                          : ActionButtonVariants.PRIMARY
                      }
                      legacyStyle={Boolean(
                        !enableABTestingOfferAndRewardDetailsButtons?.onlineOrderButton
                      )}
                    >
                      {redeemText}
                    </StyledActionButton>
                  )}
                </>
              )}
              {canAddToInRestaurantOrder && !showRemoveRewardButton && (
                <>
                  {enableABTestingOfferAndRewardDetailsButtonsDesign ? (
                    <StyledLoyaltyButton
                      onClick={handleInRestaurantRedemptionClick}
                      disabled={disableInRestaurantOrderButton}
                      data-testid="redeem-in-restaurant-btn"
                    >
                      <StyledInRestaurantIcon>
                        <IconLoyaltyMyCode />
                      </StyledInRestaurantIcon>
                      <StyledLoyaltyButtonText>
                        <StyledLoyaltyButtonTitle>
                          {formatMessage({ id: 'loyaltyRedeemScanInRestaurant' })}
                        </StyledLoyaltyButtonTitle>
                        <StyledLoyaltyButtonSubtitle>
                          {formatMessage({ id: 'loyaltyRedeemScanInRestaurantDescription' })}
                        </StyledLoyaltyButtonSubtitle>
                      </StyledLoyaltyButtonText>
                    </StyledLoyaltyButton>
                  ) : (
                    <StyledSecondaryActionButton
                      disabled={disableInRestaurantOrderButton}
                      onClick={handleInRestaurantRedemptionClick}
                      isLoading={checkingAvailability}
                      data-testid="redeem-in-restaurant-btn"
                      startIcon={
                        enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton ? (
                          <StyledMyCodeIcon fill={myCodeIconFill} />
                        ) : null
                      }
                      variant={
                        canAddToMobileOrder
                          ? enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton
                            ? isReversedButtons
                              ? ActionButtonVariants.PRIMARY
                              : ActionButtonVariants.OUTLINE
                            : ActionButtonVariants.TEXT_ONLY
                          : ActionButtonVariants.PRIMARY
                      }
                      legacyStyle={Boolean(
                        !enableABTestingOfferAndRewardDetailsButtons?.inRestaurantButton
                      )}
                    >
                      {inRestaurantRedemptionText}
                    </StyledSecondaryActionButton>
                  )}
                </>
              )}
            </ButtonContainer>
          )}

          {!isUpcomingOffer && showRemoveRewardButton && (
            <ButtonContainer>
              <RemoveRewardsButton
                onClick={handleRemoveRewardsClick}
                variant={ActionButtonVariants.OUTLINE}
              >
                {formatMessage({ id: 'removeRewardsFromCart' })}
              </RemoveRewardsButton>
            </ButtonContainer>
          )}

          {tierLocked && tierPointsNeeded! > 0 && (
            <ButtonContainer>
              <OrderMoreButton
                onClick={handleOrderMoreClick}
                variant={ActionButtonVariants.PRIMARY}
                skipLogEvent={true}
              >
                {data?.loyaltyTiersOrderMoreButtonText?.locale}
              </OrderMoreButton>
            </ButtonContainer>
          )}
        </ButtonsWrapper>

        {incentiveEvaluationResults && (
          <StyledIncentiveEvaluationMessages
            incentiveEvaluationResults={incentiveEvaluationResults}
            hideIcon
          />
        )}
        {isOffer && moreInfo && (
          <StyledDisclaimerContainer>
            <CustomDisclaimer content={moreInfo} />
          </StyledDisclaimerContainer>
        )}

        {offersInCooldown && <LoyaltyOffersCooldownDrawer />}
      </RewardWrapper>
    </Wrapper>
  );
};
